import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import styled, { createGlobalStyle } from 'styled-components';

// Styled component for image overlay
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 20px;
  border-radius: 8px;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow-y: auto;

  h4 {
    margin: 0 0 10px;
    font-size: 18px;
    color: var(--theme-color);
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 20px;
  }

  li:before {
    content: "•";
    color: var(--theme-color);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  margin: 20px 0;

  &:hover ${ImageOverlay} {
    transform: translateY(0);
    opacity: 1;
  }

  .content-image {
    display: block;
    width: 100%;
    height: auto;
  }
`;

// Global Styles
const GlobalStyles = createGlobalStyle`
  :root {
    --theme-color: #f7bf68;
  }
  /* Insurance Content Section */
  .insurance-content-section {
    padding: 80px 0;
    background-color: #f9f9f9;
  }

  .insurance-tabs {
    display: flex;
    margin-bottom: 40px;
    border-bottom: 1px solid #ddd;
    flex-wrap: wrap;
  }

  .tab-btn {
    padding: 12px 24px;
    margin-right: 10px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 16px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .tab-btn:hover,
  .tab-btn.active {
    color: var(--theme-color);
    border-bottom-color: var(--theme-color);
  }

  .content-section {
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    margin-bottom: 30px;
  }

  .section-header {
    margin-bottom: 30px;
    text-align: center;
  }

  .section-header h2 {
    color: var(--theme-color);
    margin-bottom: 10px;
  }

  .subtitle {
    color: #666;
    font-size: 18px;
  }

  .content-box {
    margin-bottom: 30px;
  }

  .highlight-box {
    background-color: #fff8ec;
    padding: 25px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  /* Tables */
  .styled-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 0.9em;
    min-width: 400px;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0,0,0,0.05);
  }

  .styled-table thead tr {
    background-color: var(--theme-color);
    color: #ffffff;
    text-align: left;
    font-weight: bold;
  }

  .styled-table th,
  .styled-table td {
    padding: 12px 15px;
  }

  .styled-table tbody tr {
    border-bottom: 1px solid #dddddd;
  }

  .styled-table tbody tr:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  .styled-table tbody tr:last-of-type {
    border-bottom: 2px solid var(--theme-color);
  }

  .styled-table tbody tr.active-row {
    font-weight: bold;
    color: var(--theme-color);
  }

  .expand-table-btn {
    background: none;
    border: none;
    color: var(--theme-color);
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 10px 0;
  }

  .expand-table-btn svg {
    margin-left: 5px;
    transition: transform 0.3s ease;
  }

  .expand-table-btn .rotate {
    transform: rotate(180deg);
  }

  .table-responsive {
    max-height: 400px;
    overflow: hidden;
    transition: max-height 0.5s ease;
  }

  .table-responsive.expanded {
    max-height: none;
  }

  /* Lists */
  .styled-list {
    list-style-type: none;
    padding-left: 0;
  }

  .styled-list li {
    position: relative;
    padding-left: 25px;
    margin-bottom: 10px;
    line-height: 1.6;
  }

  .styled-list li:before {
    content: "•";
    color: var(--theme-color);
    font-weight: bold;
    position: absolute;
    left: 0;
  }

  /* Checklist */
  .checklist {
    margin: 20px 0;
  }

  .check-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  .check-icon {
    color: var(--theme-color);
    margin-right: 10px;
    font-size: 18px;
  }

  /* Cards */
  .provider-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 30px 0;
  }

  .provider-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-top: 3px solid var(--theme-color);
  }

  .provider-card h3 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .scenario-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .scenario-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border-left: 3px solid var(--theme-color);
  }

  .insurance-types {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin: 20px 0;
  }

  .type-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    border: 1px solid #eee;
  }

  .type-card h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Pros and Cons */
  .pros-cons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    margin: 20px 0;
  }

  .pros {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .cons {
    background: #fff8ec;
    padding: 20px;
    border-radius: 8px;
    border-left: 4px solid var(--theme-color);
  }

  .pros h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  .cons h4 {
    color: var(--theme-color);
    margin-top: 0;
  }

  /* Footnotes */
  .footnote {
    font-size: 0.85em;
    color: #666;
    font-style: italic;
    margin-top: 5px;
  }

  /* Image Styling */
  .content-image {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .insurance-tabs {
      flex-direction: column;
    }
    
    .tab-btn {
      margin-bottom: 10px;
      text-align: left;
    }
    
    .pros-cons {
      grid-template-columns: 1fr;
    }
    
    .content-section {
      padding: 20px;
    }

    .content-image {
      max-width: 100%;
    }
  }
`;

export default function ManageYourCredit() {
    const [isLoading, setIsLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('borrower');
    const [expandedTables, setExpandedTables] = useState({});

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false)
        }, 2000);
    }, []);

    const toggleTableExpand = (tableId) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableId]: !prev[tableId]
        }));
    };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const sectionVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    return (
        <div>
            <GlobalStyles />
            <div className="boxed_wrapper ltr">
                {isLoading && <div className="loader-wrap">
                    <div className="preloader">
                        <div className="preloader-close">close</div>
                        <div id="handle-preloader" className="handle-preloader">
                            <div className="animation-preloader">
                                <div className="txt-loading">
                                    <img src="assets/images/animation_logo.gif" alt="img" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>}

                <Header />

                {/* page-title */}
                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{
                            backgroundImage: `url("/assets/images/background/page-title.jpg")`,
                        }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1 style={{ textAlign: "left" }}>Manage Your Credit</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Manage Your Credit</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* Page Title End */}

                {/* Main Content Section */}
                <section className="insurance-content-section">
                    <div className="auto-container">
                        {/* Navigation Tabs */}
                        <motion.div 
                            className="insurance-tabs"
                            initial="hidden"
                            animate="visible"
                            variants={fadeIn}
                        >
                            <button 
                                className={`tab-btn ${activeSection === 'borrower' ? 'active' : ''}`}
                                onClick={() => setActiveSection('borrower')}
                            >
                                Borrower Guide
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'personal' ? 'active' : ''}`}
                                onClick={() => setActiveSection('personal')}
                            >
                                Personal Credit
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'business' ? 'active' : ''}`}
                                onClick={() => setActiveSection('business')}
                            >
                                Business Credit
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'accelerator' ? 'active' : ''}`}
                                onClick={() => setActiveSection('accelerator')}
                            >
                                Business Credit Accelerator
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'monitoring' ? 'active' : ''}`}
                                onClick={() => setActiveSection('monitoring')}
                            >
                                Credit Monitoring
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'repair' ? 'active' : ''}`}
                                onClick={() => setActiveSection('repair')}
                            >
                                Credit Repair
                            </button>
                            <button 
                                className={`tab-btn ${activeSection === 'credit-trends' ? 'active' : ''}`}
                                onClick={() => setActiveSection('credit-trends')}
                            >
                                Credit Trends
                            </button>
                        </motion.div>

                        {/* Borrower Guide Section */}
                        {activeSection === 'borrower' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Understanding the 5 C's of Credit</h2>
                                    <p className="subtitle">What Every Borrower Should Know</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>The 5 C's of Credit Overview</h3>
                                    <p>When lenders evaluate your loan application, they assess five key areas—known as the 5 C's of Credit—to understand your risk profile. At Ravline Corp, we guide you through each factor to improve your approval chances.</p>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Character</h4>
                                            <p>Your credit history and reputation as a borrower.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Capacity</h4>
                                            <p>Your ability to repay based on income and debt.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Capital</h4>
                                            <p>Your financial assets or net worth.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Collateral</h4>
                                            <p>Assets pledged to secure the loan.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Conditions</h4>
                                            <p>Loan purpose and economic climate.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/5c's.jpg" 
                                            alt="5 C's of Credit illustration" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>The 5 C's of Credit</h4>
                                            <ul>
                                                <li>Character: Build a strong payment history.</li>
                                                <li>Capacity: Maintain a low debt-to-income ratio.</li>
                                                <li>Capital: Document your savings or assets.</li>
                                                <li>Collateral: Offer assets to secure loans.</li>
                                                <li>Conditions: Align loan purpose with market trends.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Details of the 5 C's</h3>
                                    <div className={`table-responsive ${expandedTables['fiveCsTable'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Credit Factor</th>
                                                    <th>Description</th>
                                                    <th>How Ravline Helps</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Character</td>
                                                    <td>Reflects your credit history and payment reliability.</td>
                                                    <td>Builds payment records, corrects past mistakes.</td>
                                                </tr>
                                                <tr>
                                                    <td>Capacity</td>
                                                    <td>Measures ability to repay via debt-to-income ratio.</td>
                                                    <td>Provides income analysis and financial coaching.</td>
                                                </tr>
                                                <tr>
                                                    <td>Capital</td>
                                                    <td>Financial assets or net worth you bring.</td>
                                                    <td>Documents and grows your capital profile.</td>
                                                </tr>
                                                <tr>
                                                    <td>Collateral</td>
                                                    <td>Assets pledged to secure the loan.</td>
                                                    <td>Advises on structuring collateral presentation.</td>
                                                </tr>
                                                <tr>
                                                    <td>Conditions</td>
                                                    <td>Loan purpose, industry, and economic trends.</td>
                                                    <td>Tailors applications to align with conditions.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('fiveCsTable')}
                                    >
                                        {expandedTables['fiveCsTable'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['fiveCsTable'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why the 5 C's Matter</h3>
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Benefits of Understanding the 5 C's:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Improves your loan approval chances</li>
                                            <li>Secures better interest rates and terms</li>
                                            <li>Reduces lender risk perception</li>
                                            <li>Guides financial planning and growth</li>
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Contact Ravline Corp</h3>
                                    <p>Ravline Corp is dedicated to empowering clients for financial growth. Led by Luneida V. Augustin (CEO) and Rodney C. Rutty (CFO), we offer personalized guidance to strengthen your borrowing profile.</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Email</h4>
                                            <p><a href="mailto:ravlinecorp@gmail.com">ravlinecorp@gmail.com</a><br /><a href="mailto:rodneyrutty@ravlinecorp.com">rodneyrutty@ravlinecorp.com</a></p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Phone</h4>
                                            <p>954-709-4029<br />305-970-7421</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Website</h4>
                                            <p><a href="https://www.ravlinecorp.com" target="_blank" rel="noopener noreferrer">www.ravlinecorp.com</a></p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Personal Credit Section */}
                        {activeSection === 'personal' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Personal Credit Solutions</h2>
                                    <p className="subtitle">Build, repair, and protect your personal credit score</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Getting Approved with a 500 Credit Score</h3>
                                    <p>Even with poor credit, you may qualify for unsecured credit cards to help rebuild your score.</p>
                                    
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Tips to Boost Approval Odds:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Apply for cards tailored to credit rebuilding</li>
                                            <li>Keep your debt-to-income ratio low</li>
                                            <li>Avoid multiple credit applications</li>
                                            <li>Fix errors in your credit report</li>
                                        </ul>
                                    </div>

                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>How We Help:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Correct inaccurate credit items</li>
                                            <li>Build positive credit behaviors</li>
                                            <li>Identify suitable financial tools</li>
                                        </ul>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/MYC1.png" 
                                            alt="Credit card for rebuilding credit" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Rebuilding Credit with a Low Score</h4>
                                            <ul>
                                                <li>Apply for secured or subprime cards designed for rebuilding.</li>
                                                <li>Maintain a low debt-to-income ratio to improve approval odds.</li>
                                                <li>Avoid multiple applications to prevent hard inquiries.</li>
                                                <li>Correct errors on your credit report for accurate scoring.</li>
                                                <li>Partner with us for tailored financial tools and strategies.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Which Bills Affect Your Credit Score?</h3>
                                    <div className={`table-responsive ${expandedTables['billsTable'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Bill Type</th>
                                                    <th>Impact</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Credit cards</td>
                                                    <td>Direct</td>
                                                    <td>Reported to all bureaus</td>
                                                </tr>
                                                <tr>
                                                    <td>Auto loans</td>
                                                    <td>Direct</td>
                                                    <td>Affects payment history</td>
                                                </tr>
                                                <tr>
                                                    <td>Mortgages</td>
                                                    <td>Direct</td>
                                                    <td>Major impact on score</td>
                                                </tr>
                                                <tr>
                                                    <td>Student loans</td>
                                                    <td>Direct</td>
                                                    <td>Reported monthly</td>
                                                </tr>
                                                <tr>
                                                    <td>Personal loans</td>
                                                    <td>Direct</td>
                                                    <td>Affects credit mix</td>
                                                </tr>
                                                <tr>
                                                    <td>Rent</td>
                                                    <td>Indirect</td>
                                                    <td>Only if reported or in collections</td>
                                                </tr>
                                                <tr>
                                                    <td>Utilities</td>
                                                    <td>Indirect</td>
                                                    <td>Only affects if unpaid</td>
                                                </tr>
                                                <tr>
                                                    <td>Cell phone</td>
                                                    <td>Indirect</td>
                                                    <td>Only if sent to collections</td>
                                                </tr>
                                                <tr>
                                                    <td>Medical bills</td>
                                                    <td>Indirect</td>
                                                    <td>Only if unpaid</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('billsTable')}
                                    >
                                        {expandedTables['billsTable'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['billsTable'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Credit Scores Differ</h3>
                                    <ul className="styled-list">
                                        <li><strong>Different Credit Bureaus:</strong> Experian, TransUnion, and Equifax may have different data</li>
                                        <li><strong>Scoring Models:</strong> FICO and VantageScore use different algorithms</li>
                                        <li><strong>Timing:</strong> Reports update continuously</li>
                                        <li><strong>Data Reporting:</strong> Not all lenders report to all bureaus</li>
                                    </ul>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/MYC2.png" 
                                            alt="Credit bureaus comparison" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Why Credit Scores Vary</h4>
                                            <ul>
                                                <li>Each bureau (Experian, TransUnion, Equifax) collects unique data.</li>
                                                <li>FICO and VantageScore apply different scoring algorithms.</li>
                                                <li>Continuous report updates cause timing differences.</li>
                                                <li>Inconsistent lender reporting affects bureau data.</li>
                                                <li>Monitor regularly to align and correct variations.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Common Reasons for Credit Score Drops</h3>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Payment Factors</h4>
                                            <ul className="styled-list">
                                                <li>Missed or late payments</li>
                                                <li>Collections or charge-offs</li>
                                            </ul>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Credit Usage</h4>
                                            <ul className="styled-list">
                                                <li>Increased credit card balances</li>
                                                <li>High credit utilization</li>
                                            </ul>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Account Changes</h4>
                                            <ul className="styled-list">
                                                <li>Closed credit accounts</li>
                                                <li>Changes in credit mix or age</li>
                                            </ul>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Other Factors</h4>
                                            <ul className="styled-list">
                                                <li>Hard inquiries (new credit applications)</li>
                                                <li>Identity theft or fraud</li>
                                                <li>Errors in credit report</li>
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>How Long to Build Good Credit?</h3>
                                    <div className={`table-responsive ${expandedTables['creditTimeline'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Starting Point</th>
                                                    <th>Timeframe</th>
                                                    <th>Key Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>No Credit</td>
                                                    <td>3-6 months</td>
                                                    <td>Open accounts, make on-time payments</td>
                                                </tr>
                                                <tr>
                                                    <td>Bad Credit (580)</td>
                                                    <td>12-24 months</td>
                                                    <td>Repair negative items, rebuild history</td>
                                                </tr>
                                                <tr>
                                                    <td>Fair Credit (580-669)</td>
                                                    <td>6-12 months</td>
                                                    <td>Manage utilization, maintain payments</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('creditTimeline')}
                                    >
                                        {expandedTables['creditTimeline'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['creditTimeline'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Business Credit Section */}
                        {activeSection === 'business' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Business Credit Solutions</h2>
                                    <p className="subtitle">Establish and build strong business credit to access funding and better terms</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Establishing Business Credit - The Ravline Way</h3>
                                    <ol className="styled-list">
                                        <li><strong>Structure Your Business:</strong> Form an LLC, S-Corp, or other legal entity</li>
                                        <li><strong>Get an EIN:</strong> Employer Identification Number from IRS</li>
                                        <li><strong>Open a Business Bank Account:</strong> Separate from personal finances</li>
                                        <li><strong>Set Up Business Phone:</strong> Listed under your company name</li>
                                        <li><strong>Build Credit Profile:</strong> Work with vendors that report to bureaus</li>
                                    </ol>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/MYC3.png" 
                                            alt="Business credit building" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Building Business Credit</h4>
                                            <ul>
                                                <li>Form an LLC or S-Corp for a legal business entity.</li>
                                                <li>Secure an EIN from the IRS for identification.</li>
                                                <li>Open a separate business bank account.</li>
                                                <li>Register a business phone under your company name.</li>
                                                <li>Collaborate with reporting vendors to build your credit profile.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Dun & Bradstreet vs. Nav.com</h3>
                                    <div className={`table-responsive ${expandedTables['dnbVsNav'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Feature</th>
                                                    <th>Dun & Bradstreet</th>
                                                    <th>Nav.com</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Credit Building</td>
                                                    <td>DUNS + PAYDEX Score</td>
                                                    <td>Nav Boost Tradeline</td>
                                                </tr>
                                                <tr>
                                                    <td>Monitoring</td>
                                                    <td>D&B Risk + PAYDEX</td>
                                                    <td>Biz + Personal Scores</td>
                                                </tr>
                                                <tr>
                                                    <td>Funding Access</td>
                                                    <td>Credibility for lenders</td>
                                                    <td>Loan & Card Marketplace</td>
                                                </tr>
                                                <tr>
                                                    <td>Gov't Compliance</td>
                                                    <td>YES - DUNS Required</td>
                                                    <td>NO - Private Sector Focused</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('dnbVsNav')}
                                    >
                                        {expandedTables['dnbVsNav'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['dnbVsNav'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Improving Your Business Credit Score</h3>
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Key Factors:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Payment history (most important)</li>
                                            <li>Credit utilization (balance vs limit)</li>
                                            <li>Length of credit history</li>
                                            <li>Credit mix (loans & revolving accounts)</li>
                                            <li>New credit inquiries</li>
                                        </ul>
                                    </div>

                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Our Strategies:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Verify profile across all bureaus</li>
                                            <li>Correct errors and outdated info</li>
                                            <li>Add vendors that report payments</li>
                                            <li>Monitor score strategically</li>
                                            <li>Time funding applications</li>
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Bank Credit Scores Explained</h3>
                                    <div className={`table-responsive ${expandedTables['bankScores'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Aspect</th>
                                                    <th>Personal Credit</th>
                                                    <th>Business Credit</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Score Range</td>
                                                    <td>300-850</td>
                                                    <td>1-100</td>
                                                </tr>
                                                <tr>
                                                    <td>Major Bureaus</td>
                                                    <td>Experian, Equifax, TransUnion</td>
                                                    <td>Experian, Equifax, D&B</td>
                                                </tr>
                                                <tr>
                                                    <td>Key Factors</td>
                                                    <td>Payment history, utilization</td>
                                                    <td>Payment history, company size</td>
                                                </tr>
                                                <tr>
                                                    <td>Impact</td>
                                                    <td>Loans, credit cards</td>
                                                    <td>Business loans, vendor terms</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('bankScores')}
                                    >
                                        {expandedTables['bankScores'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['bankScores'] ? 'rotate' : ''} />
                                    </button>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Business Credit Accelerator Section - 90-Day Program */}
                        {activeSection === 'accelerator' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>90-Day Business Credit Accelerator Program</h2>
                                    <p className="subtitle">A structured path to establish and grow your business credit in 90 days</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is the 90-Day Business Credit Accelerator?</h3>
                                    <p>Our <strong>90-Day Business Credit Accelerator</strong> is a focused program designed to help you build a strong business credit profile quickly. Whether you're a new entity or an existing business with little or no business credit, we guide you step-by-step through foundation setup, vendor relationships, and bureau reporting—so you're in a better position to access funding and favorable terms.</p>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Foundation (Days 1–30)</h4>
                                            <p>Business structure, EIN, business bank account, DUNS number, and initial vendor/net-30 accounts.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Build (Days 31–60)</h4>
                                            <p>Add reporting vendors, manage payments on time, and establish a visible profile at the major business bureaus.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Accelerate (Days 61–90)</h4>
                                            <p>Optimize your profile, correct errors, and position for business cards, lines of credit, or loans.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-credit-accelerator-1.jpg" 
                                            alt="90-Day Business Credit Accelerator program overview" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>90-Day Phases</h4>
                                            <ul>
                                                <li>Days 1–30: Foundation — entity, EIN, bank, DUNS, net-30.</li>
                                                <li>Days 31–60: Build — reporting vendors, on-time payments.</li>
                                                <li>Days 61–90: Accelerate — optimize and prepare for funding.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What You Get</h3>
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Included in the program:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Step-by-step roadmap aligned with the 90-day timeline</li>
                                            <li>Guidance on business structure, EIN, and business banking</li>
                                            <li>DUNS registration and business bureau profile setup</li>
                                            <li>Vendor and net-30 account strategies that report to bureaus</li>
                                            <li>Payment and utilization best practices</li>
                                            <li>Ongoing support to stay on track and troubleshoot</li>
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Who It's For</h3>
                                    <p>The 90-Day Business Credit Accelerator is ideal for:</p>
                                    <ul className="styled-list">
                                        <li>New LLCs or corporations with no business credit yet</li>
                                        <li>Existing businesses that haven’t built a separate business credit profile</li>
                                        <li>Owners who want a clear, time-bound plan instead of guessing</li>
                                        <li>Anyone preparing to apply for business loans, lines of credit, or cards</li>
                                    </ul>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/business-credit-accelerator-2.jpg" 
                                            alt="Who the Business Credit Accelerator is for" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Ideal For</h4>
                                            <ul>
                                                <li>New entities with no business credit.</li>
                                                <li>Existing businesses without a credit profile.</li>
                                                <li>Owners who want a clear 90-day plan.</li>
                                                <li>Those preparing for funding applications.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Get Started with the 90-Day Accelerator</h3>
                                    <p>Ravline Corp is here to help you execute the 90-Day Business Credit Accelerator with structure and support. Reach out to learn more and begin your timeline.</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Email</h4>
                                            <p><a href="mailto:ravlinecorp@gmail.com">ravlinecorp@gmail.com</a><br /><a href="mailto:rodneyrutty@ravlinecorp.com">rodneyrutty@ravlinecorp.com</a></p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Phone</h4>
                                            <p>954-709-4029<br />305-970-7421</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Website</h4>
                                            <p><a href="https://www.ravlinecorp.com" target="_blank" rel="noopener noreferrer">www.ravlinecorp.com</a></p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Credit Monitoring Section */}
                        {activeSection === 'monitoring' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Credit Monitoring Services</h2>
                                    <p className="subtitle">Stay informed and protect your credit profile</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is Credit Monitoring?</h3>
                                    <p>A service that tracks your credit reports and scores, alerting you to key changes:</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>New Account Openings</h4>
                                            <p>Alerts when new credit accounts appear in your name</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Credit Inquiries</h4>
                                            <p>Notifies you of hard inquiries from lenders</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Late Payments</h4>
                                            <p>Flags delinquencies before they hurt your score</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Balance Changes</h4>
                                            <p>Tracks credit utilization changes</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Public Records</h4>
                                            <p>Alerts about collections or judgments</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/MYC4.png" 
                                            alt="Credit monitoring dashboard" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Benefits of Credit Monitoring</h4>
                                            <ul>
                                                <li>Detects fraud early to protect your identity.</li>
                                                <li>Tracks score improvements over time.</li>
                                                <li>Highlights actions impacting your score.</li>
                                                <li>Catches report errors for quick correction.</li>
                                                <li>Offers daily updates and customized alerts.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Why Monitoring Matters</h3>
                                    <div className="pros-cons">
                                        <div className="pros">
                                            <h4>Benefits</h4>
                                            <ul className="styled-list">
                                                <li>Detects fraud early</li>
                                                <li>Tracks score improvements</li>
                                                <li>Shows what actions help/hurt your score</li>
                                                <li>Helps catch mistakes quickly</li>
                                            </ul>
                                        </div>
                                        <div className="cons">
                                            <h4>Our Monitoring Services</h4>
                                            <ul className="styled-list">
                                                <li>Daily score tracking</li>
                                                <li>Access to all 3 bureaus</li>
                                                <li>Identity theft protection</li>
                                                <li>Customized alerts</li>
                                            </ul>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Protecting Credit During Economic Uncertainty</h3>
                                    <ul className="styled-list">
                                        <li>Monitor credit weekly using monitoring tools</li>
                                        <li>Pay bills on time - even minimum payments</li>
                                        <li>Keep credit utilization below 30%</li>
                                        <li>Avoid closing old accounts</li>
                                        <li>Limit new credit applications</li>
                                        <li>Build emergency savings</li>
                                    </ul>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Credit Repair Section */}
                        {activeSection === 'repair' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Credit Repair & Restoration</h2>
                                    <p className="subtitle">Correct inaccuracies and rebuild your credit health</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>What Is Credit Repair?</h3>
                                    <p>The process of reviewing your credit reports, identifying inaccurate or outdated information, and working to remove or correct those items.</p>
                                    
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Items We Can Address:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Late payments</li>
                                            <li>Collections</li>
                                            <li>Charge-offs</li>
                                            <li>Bankruptcies</li>
                                            <li>Duplicate accounts</li>
                                            <li>Incorrect personal information</li>
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>The Credit Repair Process</h3>
                                    <ol className="styled-list">
                                        <li><strong>Review:</strong> Your credit reports from all three bureaus</li>
                                        <li><strong>Identify:</strong> Negative or inaccurate items</li>
                                        <li><strong>File Disputes:</strong> With supporting documentation</li>
                                        <li><strong>Monitor:</strong> Updates from the bureaus</li>
                                        <li><strong>Rebuild:</strong> With positive financial behaviors</li>
                                    </ol>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/MYC5.png" 
                                            alt="Credit repair process illustration" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Credit Repair Process</h4>
                                            <ul>
                                                <li>Review reports from all three bureaus for accuracy.</li>
                                                <li>Identify negative items like late payments or errors.</li>
                                                <li>File disputes with supporting evidence.</li>
                                                <li>Monitor bureau updates to ensure corrections.</li>
                                                <li>Rebuild with timely payments and low utilization.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>How We Make It Simple</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Access to Tools</h4>
                                            <p>Industry-compliant credit repair tools</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Step-by-Step Guidance</h4>
                                            <p>Through the dispute process</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Financial Coaching</h4>
                                            <p>Ongoing education and support</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Long-Term Strategies</h4>
                                            <p>To build and maintain good credit</p>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>The Importance of Credit Restoration</h3>
                                    <p>Your credit score affects loan approvals, interest rates, rental applications, employment opportunities, and even insurance premiums. Credit restoration can open new doors whether you've faced bankruptcy, missed payments, or identity theft.</p>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Credit Trends Section */}
                        {activeSection === 'credit-trends' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Credit Trends for 2026</h2>
                                    <p className="subtitle">What to expect in credit, lending, and financial health this year</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Where Credit Stands Today</h3>
                                    <p>Understanding current credit trends helps you make smarter decisions and stay ahead. Here’s a snapshot of the landscape heading into 2026.</p>
                                    <div className="scenario-cards">
                                        <div className="scenario-card">
                                            <h4>Average U.S. FICO Score</h4>
                                            <p><strong>715</strong> (Experian, Q3 2024) — scores remain near historic highs as consumers prioritize repayment.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Credit Card Utilization</h4>
                                            <p><strong>29%</strong> — keeping utilization below 30% supports stronger scores and better approval odds.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Good Credit (670+)</h4>
                                            <p><strong>71%</strong> of consumers now have good credit or better, opening doors to better rates and products.</p>
                                        </div>
                                        <div className="scenario-card">
                                            <h4>Score Awareness</h4>
                                            <p><strong>72%</strong> of adults are aware of their score (Ravline Survey) — knowledge is the first step to improvement.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/credit-trends-1.png" 
                                            alt="Credit trends 2026 overview" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>2026 Credit Snapshot</h4>
                                            <ul>
                                                <li>Average FICO score holding near 715.</li>
                                                <li>Utilization trends support healthy credit habits.</li>
                                                <li>Majority of consumers in good-credit range.</li>
                                                <li>Growing awareness drives better financial choices.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Credit Scores by Generation</h3>
                                    <p>Generational differences in credit health reflect life stage, access to credit education, and economic cycles.</p>
                                    <div className={`table-responsive ${expandedTables['genScores'] ? 'expanded' : ''}`}>
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>Generation</th>
                                                    <th>Age Range</th>
                                                    <th>Average Score</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Gen Z</td>
                                                    <td>18–27</td>
                                                    <td>681</td>
                                                    <td>Building history; early focus on credit education pays off.</td>
                                                </tr>
                                                <tr>
                                                    <td>Millennials</td>
                                                    <td>28–43</td>
                                                    <td>691</td>
                                                    <td>Recovering from recession; more prime and near-prime.</td>
                                                </tr>
                                                <tr>
                                                    <td>Gen X</td>
                                                    <td>44–59</td>
                                                    <td>706</td>
                                                    <td>Peak borrowing years; mortgages and refinancing.</td>
                                                </tr>
                                                <tr>
                                                    <td>Baby Boomers</td>
                                                    <td>60+</td>
                                                    <td>745</td>
                                                    <td>Longest history; typically strongest profiles.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button 
                                        className="expand-table-btn" 
                                        onClick={() => toggleTableExpand('genScores')}
                                    >
                                        {expandedTables['genScores'] ? 'Show Less' : 'Show More'} <FiChevronDown className={expandedTables['genScores'] ? 'rotate' : ''} />
                                    </button>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/credit-trends-2.png" 
                                            alt="Generational credit insights" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>By Generation</h4>
                                            <ul>
                                                <li>Gen Z: Building foundations with education.</li>
                                                <li>Millennials: Moving into prime credit tiers.</li>
                                                <li>Gen X: Managing mortgages and refinancing.</li>
                                                <li>Boomers: Strongest average scores and history.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>Key Trends Shaping Credit in 2026</h3>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>AI & Credit Scoring</h4>
                                            <p>Lenders are using alternative data and machine learning to assess risk, helping more people qualify while managing risk.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Buy Now, Pay Later (BNPL)</h4>
                                            <p>BNPL usage is growing; some bureaus now include it in reports. Use it wisely to avoid overextension.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Open Banking & Data Sharing</h4>
                                            <p>Consumers can share bank data for a fuller picture of cash flow, potentially improving access to credit.</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Rates & Economic Conditions</h4>
                                            <p>Interest rates and inflation continue to influence approval criteria and preferred loan terms.</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/credit-trends-3.png" 
                                            alt="2026 credit and lending trends" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>2026 Trends at a Glance</h4>
                                            <ul>
                                                <li>AI and alternative data expand access to credit.</li>
                                                <li>BNPL integrated into credit reporting.</li>
                                                <li>Open banking enables better risk assessment.</li>
                                                <li>Rates and economy drive lender behavior.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box">
                                    <h3>How to Stay Ahead in 2026</h3>
                                    <div className="checklist">
                                        <div className="check-item">
                                            <FiCheck className="check-icon" />
                                            <span><strong>Actions that still matter most:</strong></span>
                                        </div>
                                        <ul className="styled-list">
                                            <li>Pay bills on time — payment history remains the top factor</li>
                                            <li>Keep credit utilization below 30%</li>
                                            <li>Monitor your reports at all three bureaus</li>
                                            <li>Limit new applications to avoid unnecessary inquiries</li>
                                            <li>Build emergency savings to avoid leaning too hard on credit</li>
                                        </ul>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/credit-trends-4.png" 
                                            alt="Staying on top of your credit in 2026" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Your 2026 Credit Checklist</h4>
                                            <ul>
                                                <li>On-time payments above all.</li>
                                                <li>Keep utilization under 30%.</li>
                                                <li>Check all three bureaus regularly.</li>
                                                <li>Apply for new credit only when needed.</li>
                                                <li>Save for emergencies to protect your score.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="content-box highlight-box">
                                    <h3>Ravline Corp & Credit Trends for 2026</h3>
                                    <p>We help you understand how today’s trends affect your profile and goals. Whether you’re building, repairing, or protecting your credit, we offer guidance aligned with the latest data and best practices.</p>
                                    <div className="provider-cards">
                                        <div className="provider-card">
                                            <h4>Email</h4>
                                            <p><a href="mailto:ravlinecorp@gmail.com">ravlinecorp@gmail.com</a><br /><a href="mailto:rodneyrutty@ravlinecorp.com">rodneyrutty@ravlinecorp.com</a></p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Phone</h4>
                                            <p>954-709-4029<br />305-970-7421</p>
                                        </div>
                                        <div className="provider-card">
                                            <h4>Website</h4>
                                            <p><a href="https://www.ravlinecorp.com" target="_blank" rel="noopener noreferrer">www.ravlinecorp.com</a></p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </div>
                </section>

                <Footer />
            </div>
        </div>
    );
}